import { Box, TextField } from "@mui/material";
import { type TimeSlot } from "@src/appV2/Shifts/types";
import { type UseModalState } from "@src/lib/ui-react";
import { useCallback } from "react";

import { APP_V2_USER_EVENTS, logEvent, useLogEffect } from "../../../../lib/analytics";
import { useToast } from "../../../../lib/Notifications/Toasts/useToast";
import { Dialog } from "../../../components/Dialog";
import { DialogFooter } from "../../../components/DialogFooter";
import { Text } from "../../../components/Text";
import { useCreateSpendingControlNotification } from "../../api/useCreateSpendingControlNotification";
import {
  getWeekStartDatesContainingShifts,
  saveSpendingLimitIncreaseRequest,
} from "../../utils/spendingLimitRequest";

export interface SpendLimitReachedShift {
  start: Date;
  end: Date;
  requirement: string;
  timeSlot: TimeSlot;
}

export interface SpendLimitReachedDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  shifts: SpendLimitReachedShift[];
  additionalHoursNeeded: number;
  onAdditionalHoursChange: (hours: number) => void;
  onRequestApproval: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function SpendLimitReachedDialog(props: SpendLimitReachedDialogProps) {
  const {
    modalState,
    workplaceId,
    shifts,
    additionalHoursNeeded,
    onAdditionalHoursChange,
    onRequestApproval,
    onCancel,
    isLoading = false,
  } = props;

  const { showSuccessToast } = useToast();
  const { mutateAsync: createNotification } = useCreateSpendingControlNotification();

  const shiftsForLogging = shifts.map((shift) => ({
    start: shift.start.toISOString(),
    end: shift.end.toISOString(),
    requirement: shift.requirement,
    timeSlot: shift.timeSlot,
  }));

  useLogEffect(
    APP_V2_USER_EVENTS.SPENDING_CONTROL_LIMIT_REACHED_MODAL_OPENED,
    {
      workplaceId,
      shifts: shiftsForLogging,
      additionalHoursNeeded,
    },
    { enabled: modalState.modalIsOpen }
  );

  function handleCancel() {
    logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_LIMIT_REACHED_MODAL_CLOSED, {
      workplaceId,
      shifts: shiftsForLogging,
      additionalHoursNeeded,
    });
    onCancel?.();
    modalState.closeModal();
  }

  async function handleRequestApproval() {
    // Mark all weeks containing shifts as having a pending request
    const weekStartDates = getWeekStartDatesContainingShifts(shifts);
    for (const weekStartDate of weekStartDates) {
      saveSpendingLimitIncreaseRequest(workplaceId, weekStartDate, additionalHoursNeeded);
    }

    // Send Slack notification for the increase request
    // Use the first week start date as representative
    // Notification failure should not block the user flow
    const [firstWeekStartDate] = weekStartDates;
    if (firstWeekStartDate) {
      try {
        await createNotification({
          workplaceId,
          event: "increaseRequested",
          weekStartDate: firstWeekStartDate,
          additionalHoursRequested: additionalHoursNeeded,
        });
      } catch {
        // Notification failure is non-critical, continue with the flow
      }
    }

    logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_LIMIT_REACHED_REQUEST_SUBMITTED, {
      workplaceId,
      shifts: shiftsForLogging,
      additionalHoursNeeded,
    });
    showSuccessToast("Spend increase request submitted.");
    onRequestApproval();
  }

  const handleHoursChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseInt(event.target.value, 10);
      onAdditionalHoursChange(Number.isNaN(value) ? 0 : value);
    },
    [onAdditionalHoursChange]
  );

  return (
    <Dialog
      footerContent={
        <DialogFooter
          primaryAction={{
            label: "Request approval",
            onClick: handleRequestApproval,
            isLoading,
          }}
          secondaryAction={{
            label: "Cancel",
            onClick: handleCancel,
          }}
        />
      }
      modalState={modalState}
      title="Spend limit reached for the week of the shift"
      width={450}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Text sx={(theme) => ({ marginBottom: theme.spacing(2) })} variant="body1">
          Creating this shift exceeds the weekly spend limit set for your workplace. How many
          additional hours are needed for the week?
        </Text>

        <TextField
          fullWidth
          inputProps={{ min: 0 }}
          label="Additional hours needed"
          type="number"
          value={additionalHoursNeeded}
          onChange={handleHoursChange}
        />
      </Box>
    </Dialog>
  );
}
